// frontend/pages/Reports.jsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  ChartBarIcon, 
  DocumentArrowDownIcon, 
  PrinterIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { reportService } from '../services/api';
import SalesReport from '../components/Reports/SalesReport';
import TaxReport from '../components/Reports/TaxReport';
import AgingReport from '../components/Reports/AgingReport';
import ProfitLossReport from '../components/Reports/ProfitLossReport';
import StockReport from '../components/Reports/StockReport';
import ZReport from '../components/Reports/ZReport';
import ServiceReport from '../components/Reports/ServiceReport';
import { toast } from 'react-hot-toast';

const reportTabs = [
  { id: 'sales', name: 'Sales Report', icon: ShoppingBagIcon, component: SalesReport },
  { id: 'services', name: 'Services Report', icon: WrenchScrewdriverIcon, component: ServiceReport },
  { id: 'tax', name: 'Tax Report (VAT)', icon: ChartBarIcon, component: TaxReport },
  { id: 'aging', name: 'Aging Report', icon: UserGroupIcon, component: AgingReport },
  { id: 'profitloss', name: 'Profit & Loss', icon: ChartBarIcon, component: ProfitLossReport },
  { id: 'stock', name: 'Stock Report', icon: TruckIcon, component: StockReport },
  { id: 'zreport', name: 'Z-Report (Daily)', icon: ChartBarIcon, component: ZReport }
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('sales');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState(null);

  // Fetch summary data for the header
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['report-summary', dateRange],
    queryFn: () => reportService.getReportSummary(dateRange),
    enabled: true
  });

  const ActiveComponent = reportTabs.find(tab => tab.id === activeTab)?.component;

  const handleExport = async (format = 'excel') => {
    setExporting(true);
    try {
      const blob = await reportService.exportReport(activeTab, dateRange, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab}_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Report exported successfully`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Track your business performance, sales, and services</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Export Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Export PDF
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Quick Stats Cards */}
      {summaryData && !summaryLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  KES {summaryData.totalRevenue?.toLocaleString() || 0}
                </p>
              </div>
              <ShoppingBagIcon className="h-8 w-8 text-green-500" />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Products: KES {summaryData.productRevenue?.toLocaleString() || 0} | 
              Services: KES {summaryData.serviceRevenue?.toLocaleString() || 0}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Transactions</p>
                <p className="text-2xl font-bold text-blue-600">
                  {summaryData.totalTransactions?.toLocaleString() || 0}
                </p>
              </div>
              <DocumentArrowDownIcon className="h-8 w-8 text-blue-500" />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Invoices: {summaryData.invoiceCount || 0} | POS: {summaryData.posCount || 0}
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Services Revenue</p>
                <p className="text-2xl font-bold text-purple-600">
                  KES {summaryData.serviceRevenue?.toLocaleString() || 0}
                </p>
              </div>
              <WrenchScrewdriverIcon className="h-8 w-8 text-purple-500" />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {summaryData.serviceCount || 0} service transactions
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Outstanding Balance</p>
                <p className="text-2xl font-bold text-red-600">
                  KES {summaryData.outstandingBalance?.toLocaleString() || 0}
                </p>
              </div>
              <UserGroupIcon className="h-8 w-8 text-red-500" />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {summaryData.overdueCount || 0} overdue invoices
            </div>
          </div>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <button
              onClick={() => {
                const today = new Date();
                setDateRange({
                  startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
                  endDate: today.toISOString().split('T')[0]
                });
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              This Month
            </button>
          </div>
          <div>
            <button
              onClick={() => {
                const today = new Date();
                const lastMonth = new Date(today);
                lastMonth.setMonth(lastMonth.getMonth() - 1);
                setDateRange({
                  startDate: lastMonth.toISOString().split('T')[0],
                  endDate: today.toISOString().split('T')[0]
                });
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex space-x-6 min-w-max">
          {reportTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Active Report Component */}
      <div className="bg-white rounded-lg shadow">
        {ActiveComponent ? (
          <ActiveComponent dateRange={dateRange} />
        ) : (
          <div className="p-8 text-center text-gray-500">
            Select a report tab to view data
          </div>
        )}
      </div>
    </div>
  );
}